import { devices_config } from '@devices/index';
import { addDate, dateFormat, getUserTimeZoneDate } from 'src/helpers/date_helper';
import { real_id } from 'src/helpers/interface_helpers';
import { getOverAllChartsConfig } from 'src/helpers/statistics/config/overall_consumption';
import { addLastHourConsumption } from 'src/helpers/statistics/core/last_hour_helpers/last_hour_consumption_helpers';
import { estimateChartConsumption } from 'src/helpers/statistics/core/consumptions/consumption_helpers';
import { parseStatisticsData } from 'src/helpers/statistics/core/parse_statistics_data';
import CacheStatisticRequest from 'src/helpers/statistics/request/chart_cache_statistics_helpers';
import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
import type { Device } from 'src/types/Device';
import type {
	GetStatsPeriodRequest,
	GetStatsResponseData,
	RequestStatisticData,
} from 'src/types/requests/charts/get_stats_period';
import { refreshLiveRangeEnd, type EnergyMonitoringPeriodId } from './period_helpers';
import type { EnergyStatisticsResult, FetchEnergyStatisticsOptions } from './fetch_energy_statistics';

const chartConfig = getOverAllChartsConfig('areaspline').charts[0];
const accountRequestUrl = getOverAllChartsConfig('areaspline').url;
const deviceRequestUrl = 'v2/statistics/power-consumption';
const inFlightRequests = new Map<string, Promise<EnergyStatisticsResult | null>>();

export type EnergyStatisticsScope =
	| { kind: 'account'; roomId?: number | null }
	| { kind: 'device'; device: Device };

export type EnergyStatisticsQuery = {
	scope: EnergyStatisticsScope;
	controls: ChartControlsDataInterface;
	periodId: EnergyMonitoringPeriodId;
	currency: string;
	options?: FetchEnergyStatisticsOptions;
};

const buildRequest = (query: EnergyStatisticsQuery) => {
	const data = refreshLiveRangeEnd(query.controls, query.periodId);
	const from = data.time.from;
	const to = data.time.to;
	const oneHour = 60 * 60 * 1000;
	const timeDiff = to.getTime() - from.getTime();
	const dailyRange = timeDiff > oneHour && timeDiff <= oneHour * 24;
	const common: RequestStatisticData = {
		date_range: dailyRange ? 'day' : 'custom',
		date_from: dateFormat(from, data.format),
		date_to: dailyRange ? undefined : dateFormat(to, data.format),
	};

	if (query.scope.kind === 'device') {
		const { device } = query.scope;
		return {
			data,
			requestUrl: deviceRequestUrl,
			server: device.shared ? device.server : undefined,
			device,
			requestData: {
				...common,
				id: real_id(device.id),
				channel: device.channel,
			} as RequestStatisticData,
		};
	}

	return {
		data,
		requestUrl: accountRequestUrl,
		server: undefined,
		device: null,
		requestData: {
			...common,
			room_id:
				query.scope.roomId != null && query.scope.roomId > -1 ? query.scope.roomId : undefined,
		} as RequestStatisticData,
	};
};

const executeQuery = async (query: EnergyStatisticsQuery): Promise<EnergyStatisticsResult | null> => {
	if (query.scope.kind === 'device' && !devices_config[query.scope.device?.type]?.isPM) return null;

	const { data, requestUrl, requestData, server, device } = buildRequest(query);
	const response: GetStatsPeriodRequest = await CacheStatisticRequest.getData(
		requestUrl,
		requestData,
		server,
	);
	if (response?.error) throw new Error('Energy statistics request failed');
	let responseData: GetStatsResponseData = response?.history
		? (response as GetStatsResponseData)
		: response?.data;
	if (!responseData?.history) return null;
	const lastHour = getUserTimeZoneDate().date;
	lastHour.setMinutes(0, 0, 0);

	if (
		query.options?.mergeLastHour !== false &&
		!/^hour$/.test(data.rangeOption.period) &&
		data.time.to.getTime() >= lastHour.getTime()
	) {
		const lastHourResponse = await CacheStatisticRequest.getData(
			requestUrl,
			{
				...requestData,
				date_range: 'custom',
				date_from: dateFormat(lastHour, data.format),
				date_to: dateFormat(addDate(lastHour, 'h'), data.format),
			},
			server,
		);
		if (!lastHourResponse?.error && lastHourResponse?.history) {
			responseData = addLastHourConsumption(
				responseData,
				lastHourResponse.history
					? (lastHourResponse as GetStatsResponseData)
					: lastHourResponse.data,
				data,
			);
		}
	}

	const chartData = parseStatisticsData(
		responseData,
		chartConfig,
		data.rangeOption.period,
		device,
		null,
		false,
	);
	if (
		query.options?.requireChartData !== false &&
		!chartData?.series?.some((series) => series.data?.length)
	) {
		return null;
	}

	return {
		chartData,
		statistic: estimateChartConsumption(chartData, '', query.currency),
		history: responseData?.history ?? [],
		devices: responseData?.devices,
		interval: responseData?.history_interval ?? responseData?.interval,
	};
};

const queryKey = (query: EnergyStatisticsQuery) => {
	const { data, requestUrl, requestData, server } = buildRequest(query);
	return JSON.stringify({
		requestUrl,
		requestData,
		server,
		period: data.rangeOption.period,
		mergeLastHour: query.options?.mergeLastHour !== false,
		requireChartData: query.options?.requireChartData !== false,
		currency: query.currency,
	});
};

/** Shares concurrent identical requests; the lower-level statistics cache owns result lifetime. */
export const getEnergyStatistics = (
	query: EnergyStatisticsQuery,
): Promise<EnergyStatisticsResult | null> => {
	const key = queryKey(query);
	const existing = inFlightRequests.get(key);
	if (existing) return existing;

	const request = executeQuery(query).finally(() => inFlightRequests.delete(key));
	inFlightRequests.set(key, request);
	return request;
};
